import { FC, useEffect } from "react";
import { EditSingleShareholderFormModel } from "./editSingleShareholderDomain";
import { observer } from "mobx-react";
import { VBox } from "../../components-v2/Box";
import { MTextInput } from "../../components-v2/TextInput";
import { EditPersonInfoForm } from "./EditPersonInfoForm";
import { MSelect } from "../../components-v2/Select";

import { useSearchStore, useTranslation } from "../../stores/RootStoreContext";
import { MRadioButtonGroup } from "../../components-v2/RadioButton";
import { EditAddressForm } from "./EditAddressForm";
import { YesNoQuestion } from "../../dbot/common/commonComponents";
import { isSwedishOrgNo } from "../../utils/orgNo";
import { Autocomplete } from "../../components-v2/Autocomplete";
import { when } from "../../utils/functions";
import { LoadingIndicator } from "../../components-v2/LoadingIndicator";

export const EditSingleShareholderForm: FC<{
  model: EditSingleShareholderFormModel;
  fetchShareholders: () => void;
  isLoadingShareholders: boolean;
  isShareholderView?: boolean;
}> = observer(({ model, fetchShareholders, isLoadingShareholders, isShareholderView }) => {
  const { t } = useTranslation();
  const { suggestedCompanies, debouncedCompanySearch, clearSuggestedCompanies } = useSearchStore();

  useEffect(() => {
    clearSuggestedCompanies();
  }, [clearSuggestedCompanies]);

  return (
    <VBox gap={"xl"}>
      {!isShareholderView && model.showField("isCompany") && (
        <VBox>
          <div>{t("shareholders.editSingle.isCompany.title")}</div>
          <MRadioButtonGroup gap={"xl"} model={model.isCompany} horizontal />
        </VBox>
      )}
      {model.isCompany.value && (
        <>
          {!isShareholderView && model.showCompanyDetails && (
            <VBox gap={"md"}>
              <strong>{t("shareholders.editSingle.companyDetails")}</strong>
              {model.showField("nationality") && (
                <MSelect
                  label={t("shareholders.editSingle.nationality")}
                  model={model.nationality}
                  fullWidth={true}
                />
              )}
              {model.showField("companyName") && (
                <>
                  <Autocomplete
                    label={t("shareholders.editSingle.companyName")}
                    inputValue={model.companyName.inputValue}
                    onInputValueChange={(value) => {
                      model.companyName.setInputValue(value);
                      debouncedCompanySearch(value);
                    }}
                    onInputBlur={() => {
                      fetchShareholders();
                      model.companyName.commit();
                    }}
                    items={
                      model.companyName.isEmpty || suggestedCompanies.length > 0
                        ? suggestedCompanies
                        : [model.selectedCompany]
                    }
                    onSelect={(company) => {
                      model.selectCompany(company);
                    }}
                    value={model.selectedCompany}
                    idFn={(item) => item.organizationnumber}
                    labelFn={(item) =>
                      item.organizationnumber === ""
                        ? item.name
                        : `${item.name} (${item.organizationnumber})`
                    }
                    onClear={() => {
                      model.selectCompany(undefined);
                      clearSuggestedCompanies();
                    }}
                    error={!model.companyName.isValid}
                    message={when(model.companyName.error, t)}
                  />
                </>
              )}
              {model.showField("companyNumber") && (
                <MTextInput
                  label={t("shareholders.editSingle.companyNumber")}
                  model={model.companyNumber}
                  onBlur={() => {
                    fetchShareholders();
                    model.companyNumber.commit();
                  }}
                />
              )}
              {isLoadingShareholders && (
                <LoadingIndicator
                  size="sm"
                  label={t("shareholders.editSingle.finding-duplicates")}
                />
              )}
              {model.showField("utlNumber") && (
                <MTextInput
                  label={t("shareholders.editSingle.utlNumber")}
                  model={model.utlNumber}
                />
              )}
              <EditAddressForm model={model.shareholderAddress} showField={model.showField} />
              {isSwedishOrgNo(model.organizationNumber) && (
                <YesNoQuestion
                  question={t("newShareholder.issuedShareholderCertificate")}
                  field={model.issuedShareholderCertificate}
                />
              )}
            </VBox>
          )}
          {model.contactInfo && model.showPersonInfo && (
            <VBox gap={"md"}>
              <strong>{t("shareholders.editSingle.primaryRepresentative")}</strong>
              <EditPersonInfoForm
                model={model.contactInfo}
                isShareholderView={isShareholderView}
                showField={model.showField}
                isSwedishOrgNo={isSwedishOrgNo(model.organizationNumber)}
              />
              {model.showField("isEmployee") && (
                <MSelect label={t("shareholders.editSingle.employee")} model={model.isEmployee} />
              )}
            </VBox>
          )}
        </>
      )}
      {!model.isCompany.value && model.personInfoModel && model.showPersonInfo && (
        <VBox gap={"md"}>
          <strong>{t("shareholders.editSingle.shareholderDetails")}</strong>
          {!isShareholderView && model.showField("nationality") && (
            <MSelect
              label={t("shareholders.editSingle.nationality")}
              model={model.nationality}
              fullWidth={true}
            />
          )}
          <EditPersonInfoForm
            model={model.personInfoModel}
            utlNumber={model.utlNumber}
            shareholderAddress={model.shareholderAddress}
            issuedShareholderCertificate={model.issuedShareholderCertificate}
            isShareholderView={isShareholderView}
            showField={model.showField}
            isSwedishOrgNo={isSwedishOrgNo(model.organizationNumber)}
            fetchShareholders={fetchShareholders}
            isLoadingShareholders={isLoadingShareholders}
          />
          {model.showField("isEmployee") && (
            <MSelect label={t("shareholders.editSingle.employee")} model={model.isEmployee} />
          )}
        </VBox>
      )}

      {model.showBankDetails && (
        <VBox>
          <strong>{t("shareholders.editSingle.bankDetails")}</strong>
          {model.showField("bic") && (
            <MTextInput label={t("shareholders.editSingle.bic")} model={model.bic} />
          )}
          {model.showField("iban") && (
            <MTextInput label={t("shareholders.editSingle.iban")} model={model.iban} />
          )}
          {model.showField("bankDetailsCountry") && (
            <MTextInput
              label={t("shareholders.editSingle.bankDetailsCountry")}
              model={model.bankDetailsCountry}
            />
          )}
        </VBox>
      )}
    </VBox>
  );
});
